import React, {useEffect, useState, useRef, ChangeEvent} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage, useIntl} from 'react-intl';

import GritxSelect from 'components/gritx-select';
import EmailZone from 'components/email-zone';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';

import {getTemplates, materialsAsyncActions} from 'redux/pageAsyncActions';
import {IStore} from 'redux/interface';
import 'react-quill/dist/quill.snow.css';
import {generateReferralHtmlTemplate} from '../../redux/api/cmService';

import './styles.scss';

interface ISubject {
  id: number
  name: string
  code: string
  template: string
  language: string
}

export interface IShareReferrals {
  templateId: number | undefined
  emailHtml: string
  toSelf: boolean
  emails: string
}

interface IReferralsShare {
  onSend: () => void
  onChange: (data: IShareReferrals) => void
  onDone: () => void
  onRemoveAll: () => void,
  onShareMode: (sharing: boolean) => void,
  isDisabledRemoveAll: boolean
  isDisabledShare: boolean
  isDisabledSend: boolean
  sendStatus: boolean
  checkedCards?: number[]
}

interface ReactQuillProps {
  [key: string]: unknown;
}

export const ReferralsShare: React.FunctionComponent<IReferralsShare> = (
  {
    onSend,
    onRemoveAll,
    onShareMode,
    isDisabledRemoveAll,
    isDisabledShare,
    isDisabledSend,
    onChange,
    sendStatus,
    onDone,
    checkedCards
  }: IReferralsShare
) => {
  const {
    shareTemplates
  } = useSelector((state: IStore) => state.content);
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [toSelf, setToSelf] = useState(false);
  const [emails, setEmails] = useState('');
  const [subject, setSubject] = useState<ISubject | null>(null);
  const [emailHtml, setEmailHtml] = useState<string>('');
  const [ReactQuill, setReactQuill] = useState<React.ComponentType<ReactQuillProps> | null>(null);

  useEffect(() => {
    dispatch(materialsAsyncActions[getTemplates]());
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setEmails('');
    setSubject(null);
    setToSelf(false);
    setEmailHtml('');
  }, [sendStatus]);

  useEffect(() => {
    onChange({
      emails,
      templateId: subject?.id,
      toSelf,
      emailHtml
    });
  }, [toSelf, emails, subject, emailHtml]);

  const getEmailHtml = async () => {
    if (subject) {
      const {data} = await generateReferralHtmlTemplate(subject.id, checkedCards || []);

      setEmailHtml(data.value);
    }
  };

  useEffect(() => {
    getEmailHtml();
  }, [subject, checkedCards]);

  useEffect(() => {
    function handleCloseTab(e: BeforeUnloadEvent) {
      if (isOpen) {
        const message = intl.formatMessage({
          id: 'gritx.common.notification.close-tab',
          defaultMessage: 'Changes you made may not be saved.'
        });

        (e || window.event).returnValue = message;

        return message;
      }

      return null;
    }

    window.addEventListener('beforeunload', handleCloseTab);

    return () => window.removeEventListener('beforeunload', handleCloseTab);
  }, [isOpen]);

  useEffect(() => {
    let canceled = false;

    if (isOpen && !ReactQuill) {
      (async () => {
        const module = await import('react-quill');

        if (canceled) return;

        const Quill = module.Quill;

        const Size = Quill.import('formats/size') as { whitelist: (string | boolean)[] };

        Size.whitelist = ['small', false, 'large', 'huge'];
        Quill.register(Size, true);

        setReactQuill(() => module.default as React.ComponentType<ReactQuillProps>);
      })();
    }

    return () => {
      canceled = true;
    };
  }, [isOpen, ReactQuill]);

  function handleClearReferrals() {
    onRemoveAll();
  }

  function handleToggleForm(value: boolean) {
    return () => {
      setIsOpen(value);
      onShareMode(value);
      if (!value) {
        setSubject(null);
        setEmails('');
        setToSelf(false);
        setEmailHtml('');
      }
    };
  }

  function handleSendForm() {
    onSend();
  }

  function handleSelectSubject(e: ChangeEvent<HTMLSelectElement>) {
    const newSubj = shareTemplates.find(subj => subj.id.toString() === e.target.value);

    if (newSubj) {
      setSubject(newSubj);
    }
  }

  function handleDone() {
    setSubject(null);
    onDone();
  }

  function updateEmailsAndToSelf(parsedEmails: string[], parsedToSelf: boolean) {
    setEmails(parsedEmails.join(','));
    setToSelf(parsedToSelf);
  }

  const modules = {
    toolbar: [
      [{size: ['small', false, 'large', 'huge']}],
      ['bold', 'italic', 'underline', 'strike'],
      [{list: 'ordered'}, {list: 'bullet'}],
      ['clean']
    ]
  };

  function renderHeader() {
    if (sendStatus) {
      return <GritxButton
        title={intl.formatMessage({
          id: 'gritx.common.button.done',
          defaultMessage: 'Done'
        })}
        className={'referrals-share__button'}
        variant={ButtonVariant.Primary}
        onClick={handleDone}
      />;
    }

    return isOpen ? <>
      <GritxButton
        title={intl.formatMessage({
          id: 'gritx.common.button.cancel',
          defaultMessage: 'Cancel'
        })}
        className={'referrals-share__button'}
        variant={ButtonVariant.Outline}
        onClick={handleToggleForm(false)}
      />
      <GritxButton
        title={intl.formatMessage({
          id: 'gritx.common.button.send',
          defaultMessage: 'Send'
        })}
        className={'referrals-share__button'}
        variant={ButtonVariant.Primary}
        disabled={isDisabledSend}
        onClick={handleSendForm}
      />
    </>
      : <>
        <GritxButton
          title={intl.formatMessage({
            id: 'gritx.referrals.removeAll',
            defaultMessage: 'Remove All Cards'
          })}
          className={'referrals-share__button'}
          variant={ButtonVariant.Outline}
          disabled={isDisabledRemoveAll}
          onClick={handleClearReferrals}
        />
        <GritxButton
          title={intl.formatMessage({
            id: 'gritx.referrals.create',
            defaultMessage: 'Create a Referral'
          })}
          className={'referrals-share__button referrals-share__create-button'}
          variant={ButtonVariant.Primary}
          disabled={isDisabledShare}
          onClick={handleToggleForm(true)}
        />
      </>;
  }

  return <div className="referrals-share">
    <div className="referrals-share__header">
      {renderHeader()}
    </div>
    {
      isOpen
      && <div className='referrals-share__body'>
        <div className="referrals-share__subject subject-selector">
          <GritxSelect
            name={'subject'}
            options={shareTemplates}
            placeholder={intl.formatMessage({
              id: 'gritx.referrals.subject',
              defaultMessage: 'Please, choose the Subject of your e-mail'
            })}
            defaultValue={'placeholder'}
            onChange={handleSelectSubject}
            onBlur={handleSelectSubject}
            className='subject-selector__selector'
          />
          {ReactQuill && <ReactQuill
            value={emailHtml}
            onChange={setEmailHtml}
            modules={modules}
            theme="snow"
            className="referrals-share__html-form"
          />}
        </div>
        <div className="referrals-share__form">
          <EmailZone
            initialEmails={emails}
            initialToSelf={toSelf}
            onChange={(parsedEmails, parsedToSelf) => updateEmailsAndToSelf(parsedEmails, parsedToSelf)}/>
        </div>
      </div>
    }
    {
      isOpen && <h2 className="referrals-share__cards-title">
        <FormattedMessage
          id={'gritx.referrals.prompt'}
          defaultMessage={'Click on the cards that you want to send'}
        />
      </h2>
    }
  </div>;
};
